import React, { useState, useCallback, useEffect } from 'react';
import { 
  Autocomplete, 
  TextField, 
  Box, 
  Typography, 
  ListItem, 
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Paper,
  Chip,
  Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { performUnifiedSearch } from '../services/searchService';
import { getTypeColor, getTypeLabel, parseDescription } from '../utils/searchUtils';

/**
 * Global search bar component for searching across all resources
 */
const SearchBar = ({ isMobile }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  // Debounced search function
  const performSearch = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const results = await performUnifiedSearch(query);
      setSearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce effect
  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(searchQuery);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchQuery, performSearch]);

  /**
   * Handle selection of a search result
   */
  const handleResultSelect = (event, value) => {
    if (!value) return;

    // Navigate based on result type
    if (value.navigationState) {
      // For PDF content matches with search highlighting
      navigate(value.navigationPath, { state: value.navigationState });
    } else if (value.scrollToId) {
      // For items that need to scroll to a specific element (like accolades)
      navigate(value.navigationPath, { state: { scrollToId: value.scrollToId } });
    } else {
      // Standard navigation
      navigate(value.navigationPath);
    }

    // Reset search
    setSearchQuery('');
    setSearchResults([]);
    setOpen(false);
  };



  return (
    <Autocomplete
      freeSolo
      open={open && searchResults.length > 0}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={searchResults}
      loading={loading}
      inputValue={searchQuery}
      onInputChange={(event, newInputValue) => {
        setSearchQuery(newInputValue);
      }}
      onChange={handleResultSelect}
      getOptionLabel={(option) => option.title || ''}
      filterOptions={(x) => x} // We handle filtering in the service
      isOptionEqualToValue={(option, value) => option.id === value.id}
      sx={{
        width: isMobile ? '100%' : { xs: 250, sm: 300, md: 400 },
        '& .MuiAutocomplete-inputRoot': {
          color: '#F5F5F5',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
          },
          '&.Mui-focused': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
          },
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'rgba(217, 141, 42, 0.3)',
        },
        '& .MuiAutocomplete-inputRoot:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: 'rgba(217, 141, 42, 0.5)',
        },
        '& .MuiAutocomplete-inputRoot.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: '#d98d2a',
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search everything..."
          size="small"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <SearchIcon sx={{ color: '#d98d2a', mr: 1 }} />
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <ListItem
          {...props}
          key={`${option.type}-${option.id}`}
          sx={{
            borderBottom: '1px solid rgba(0,0,0,0.05)',
            '&:hover': {
              backgroundColor: 'rgba(217, 141, 42, 0.08)',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <Typography fontSize="1.5rem">{option.icon}</Typography>
          </ListItemIcon>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {option.title}
                </Typography>
                <Chip
                  label={getTypeLabel(option.type)}
                  size="small"
                  sx={{
                    backgroundColor: getTypeColor(option.type),
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.7rem',
                    height: 20,
                  }}
                />
              </Box>
            }
            secondary={
              <Box>
                <Typography variant="caption" display="block" color="text.secondary">
                  {option.subtitle}
                </Typography>
                {option.description && (
                  <Typography 
                    variant="caption" 
                    display="block" 
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      maxWidth: '100%',
                    }}
                  >
                    {parseDescription(
                      option.description.length > 150 
                        ? `${option.description.substring(0, 150)}...` 
                        : option.description
                    )}
                  </Typography>
                )}
              </Box>
            }
          />
        </ListItem>
      )}
      PaperComponent={(props) => (
        <Paper
          elevation={8}
          sx={{
            mt: 1,
            maxHeight: 500,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#fff',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            border: '2px solid #d98d2a',
            overflow: 'hidden', // Prevent double scroll
          }}
        >
          <Box sx={{ 
            flex: 1, 
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#d98d2a',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#b87423',
            },
          }}>
            {props.children}
          </Box>
          {/* Show "View All Results" message if there are more results */}
          {searchResults.length > 0 && searchResults[0].hasMore && (
            <Button
              fullWidth
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/search-results?q=${encodeURIComponent(searchQuery)}`, {
                  state: { searchQuery }
                });
                setOpen(false);
              }}
              sx={{
                p: 1.5,
                textAlign: 'center',
                borderTop: '2px solid #d98d2a',
                backgroundColor: '#fff9f0',
                flexShrink: 0,
                borderRadius: 0,
                '&:hover': {
                  backgroundColor: '#fff3e0',
                },
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#d98d2a', mb: 0.5 }}>
                  Showing {searchResults.length} of {searchResults[0].totalResults} results
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Click to view all results
                </Typography>
              </Box>
            </Button>
          )}
        </Paper>
      )}
      noOptionsText={
        searchQuery.length < 2 
          ? "Type at least 2 characters to search..." 
          : "No results found"
      }
    />
  );
};

export default SearchBar;

